import { useEffect, useMemo, useState } from "react";
import {
  App,
  Avatar,
  Form,
  Input,
  Modal,
  Select,
  Space,
  Statistic,
  Typography,
  Upload,
} from "antd";
import { UserOutlined } from "@ant-design/icons";
import {
  BotPersona,
  CreateBotPayload,
  useCreateBotMutation,
  useGetCategoriesQuery,
  useGetCitiesQuery,
  useGetCountiesQuery,
  useGetCountriesQuery,
  useLazyPricingQuoteQuery,
} from "api";
import { getBase64 } from "libs";
import type { FileType } from "pages/admin/UserPage/UserInfo";
import { getErrorMessage } from "components";

interface AddBotModalProps {
  open: boolean;
  onCancel: () => void;
  onCreated?: (botId: number) => void;
  /**
   * Number of operational bots BEFORE this one is added — drives the pricing
   * preview ("you'll be on tier X after").
   */
  currentBotCount: number;
}

interface FormShape {
  name: string;
  username?: string;
  persona: BotPersona;
  target_country_id: number;
  target_county_id?: number;
  target_city_id?: number;
  target_category_ids?: number[];
}

const USER_TYPE_OPTIONS = [
  { value: 1, label: "Persoana fizica (PF)" },
  { value: 2, label: "Persoana juridica (PJ)" },
];

export const AddBotModal = ({
  open,
  onCancel,
  onCreated,
  currentBotCount,
}: AddBotModalProps) => {
  const [form] = Form.useForm<FormShape>();
  const { notification } = App.useApp();
  // Base64 data string of the chosen avatar (uploaded server-side on create).
  const [photo, setPhoto] = useState<string | undefined>(undefined);

  const [createBot, { isLoading: isCreating, error }] = useCreateBotMutation();

  const { data: countries } = useGetCountriesQuery({});
  const countryId = Form.useWatch("target_country_id", form);
  const { data: counties } = useGetCountiesQuery(
    (countryId
      ? { "filters[country_id][$eq]": countryId }
      : {}) as never,
    { skip: !countryId }
  );
  const countyId = Form.useWatch("target_county_id", form);
  const { data: cities } = useGetCitiesQuery(
    (countyId ? { "filters[county][$eq]": countyId } : {}) as never,
    { skip: !countyId }
  );
  const { data: categories } = useGetCategoriesQuery({});

  const [fetchQuote, { data: quoteData, isFetching: isQuoteFetching }] =
    useLazyPricingQuoteQuery();

  // Show the pricing impact of adding ONE more bot.
  useEffect(() => {
    if (!open) return;
    fetchQuote({ quantity: currentBotCount + 1 });
  }, [open, currentBotCount, fetchQuote]);

  const quote = quoteData?.data;
  const totalEur = useMemo(
    () => (quote ? (quote.total_cents / 100).toFixed(2) : null),
    [quote]
  );

  useEffect(() => {
    if (error) {
      notification.error({ message: getErrorMessage(error) });
    }
  }, [error, notification]);

  const onSubmit = async (values: FormShape) => {
    const payload: CreateBotPayload = {
      name: values.name,
      persona: values.persona,
      target_country_id: values.target_country_id,
      target_county_id: values.target_county_id ?? null,
      target_city_id: values.target_city_id ?? null,
      target_category_ids: values.target_category_ids ?? [],
      username: values.username?.trim()
        ? values.username.trim().toLowerCase()
        : undefined,
      profile_picture: photo,
    };
    try {
      const created = await createBot(payload).unwrap();
      notification.success({ message: `Botul "${created.name}" a fost creat.` });
      form.resetFields();
      setPhoto(undefined);
      onCreated?.(created.id);
      onCancel();
    } catch {
      // error notification handled by useEffect
    }
  };

  return (
    <Modal
      title="Adauga bot nou"
      open={open}
      onCancel={onCancel}
      onOk={() => form.submit()}
      okText="Creeaza si plateste"
      cancelText="Anuleaza"
      confirmLoading={isCreating}
      width={720}
      destroyOnClose
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={onSubmit}
        initialValues={{
          persona: { user_type: 1 },
          target_country_id: 1, // Romania default per countries seed
        }}
      >
        <Form.Item
          name="name"
          label="Nume intern"
          rules={[{ required: true, message: "Introdu numele botului" }]}
          extra="Doar pentru identificare in panoul admin. Nu este vizibil in feed."
        >
          <Input placeholder="ex: Ana Pop - Drumetii" maxLength={100} />
        </Form.Item>

        <Form.Item label="Poza de profil (optional)">
          <Upload
            accept="image/*"
            showUploadList={false}
            customRequest={({ file }) =>
              getBase64(file as FileType, (url) => setPhoto(url))
            }
          >
            <Avatar
              src={photo}
              size={72}
              icon={<UserOutlined />}
              style={{ cursor: "pointer" }}
            />
          </Upload>
          <Typography.Text
            type="secondary"
            style={{ display: "block", fontSize: 12, marginTop: 4 }}
          >
            Apasa pe avatar ca sa incarci o poza.
          </Typography.Text>
        </Form.Item>

        <Typography.Title level={5} style={{ marginTop: 24 }}>
          Persona
        </Typography.Title>
        <Space.Compact block>
          <Form.Item
            name={["persona", "display_first_name"]}
            label="Prenume afisat"
            rules={[{ required: true, message: "Required" }]}
            style={{ flex: 1, marginRight: 8 }}
          >
            <Input maxLength={50} placeholder="Ana" />
          </Form.Item>
          <Form.Item
            name={["persona", "display_last_name"]}
            label="Nume afisat"
            rules={[{ required: true, message: "Required" }]}
            style={{ flex: 1 }}
          >
            <Input maxLength={50} placeholder="Pop" />
          </Form.Item>
        </Space.Compact>

        <Form.Item
          name="username"
          label="Username (optional)"
          rules={[
            { min: 4, message: "Minim 4 caractere" },
            { max: 20, message: "Maxim 20 caractere" },
            {
              pattern: /^[a-z0-9_]+$/,
              message: "Doar litere mici, cifre si _",
            },
          ]}
          extra="Daca il lasi gol, se genereaza automat din nume. Trebuie sa fie unic."
        >
          <Input addonBefore="@" placeholder="ana_pop" maxLength={20} />
        </Form.Item>

        <Form.Item
          name={["persona", "user_type"]}
          label="Tip cont"
          rules={[{ required: true }]}
        >
          <Select options={USER_TYPE_OPTIONS} />
        </Form.Item>

        <Form.Item
          name={["persona", "bio"]}
          label="Descriere profil (bio)"
          extra="Optional. Apare pe profilul botului in app."
        >
          <Input.TextArea rows={2} maxLength={500} />
        </Form.Item>

        <Form.Item
          name={["persona", "system_prompt_fragment"]}
          label="Fragment de prompt LLM"
          rules={[
            { required: true, message: "Required" },
            { min: 20, message: "Min 20 caractere" },
            { max: 400, message: "Max 400 caractere" },
          ]}
          extra="O propozitie/doua care descrie vocea botului. Folosit de generator pt. continut."
        >
          <Input.TextArea
            rows={3}
            maxLength={400}
            placeholder='ex: "Esti o femeie de 32 de ani din Cluj, pasionata de drumetii si fotografie de natura."'
          />
        </Form.Item>

        <Typography.Title level={5} style={{ marginTop: 24 }}>
          Localizare
        </Typography.Title>
        <Form.Item
          name="target_country_id"
          label="Tara"
          rules={[{ required: true }]}
        >
          <Select
            options={(countries || []).map((c) => ({
              value: c.id,
              label: c.name,
            }))}
            showSearch
            optionFilterProp="label"
          />
        </Form.Item>
        <Form.Item name="target_county_id" label="Judet (optional)">
          <Select
            options={(counties || []).map((c) => ({
              value: c.id,
              label: c.name,
            }))}
            allowClear
            showSearch
            optionFilterProp="label"
            disabled={!countryId}
          />
        </Form.Item>
        <Form.Item name="target_city_id" label="Oras (optional)">
          <Select
            options={(cities || []).map((c) => ({
              value: c.id,
              label: c.name,
            }))}
            allowClear
            showSearch
            optionFilterProp="label"
            disabled={!countyId}
          />
        </Form.Item>

        <Form.Item
          name="target_category_ids"
          label="Categorii preferate"
          extra="Botul va posta si interactiona doar in aceste categorii."
        >
          <Select
            mode="multiple"
            options={(categories || []).map((c) => ({
              value: c.id,
              label: c.name,
            }))}
            placeholder="Selecteaza una sau mai multe"
            showSearch
            optionFilterProp="label"
          />
        </Form.Item>

        {quote && (
          <div
            style={{
              marginTop: 24,
              padding: 16,
              background: "#fafafa",
              borderRadius: 8,
            }}
          >
            <Typography.Text strong>Impact pret lunar</Typography.Text>
            <Space size="large" style={{ marginTop: 8, width: "100%" }}>
              <Statistic
                title={`Tier ${quote.tier} (la ${quote.quantity} boti)`}
                value={`${(quote.unit_price_cents / 100).toFixed(2)} ${quote.currency}`}
                suffix="/ bot"
                loading={isQuoteFetching}
              />
              <Statistic
                title={`Total lunar (cu TVA ${quote.vat_percent}%)`}
                value={`${totalEur} ${quote.currency}`}
                loading={isQuoteFetching}
              />
            </Space>
            <Typography.Paragraph
              type="secondary"
              style={{ margin: "8px 0 0", fontSize: 12 }}
            >
              Stripe va emite imediat o factura prorata pentru zilele ramase
              din ciclul curent.
            </Typography.Paragraph>
          </div>
        )}
      </Form>
    </Modal>
  );
};
